/**
 * `<policy-findings>` — the one findings table (`SC-F529`, `SC-S523`).
 *
 * The whole point of this element is the **severity distinction**, because a user
 * who cannot tell an `error` from a `blocking-warning` cannot act on either:
 *
 *   - `error` blocks the save. The object cannot be stored in this shape.
 *   - `blocking-warning` blocks the publish and allows the save. It is what an
 *     imported policy gets when the shape is real, must be preserved for the
 *     author to see, and must not reach an instance.
 *   - `warning` informs. It exists because the author is about to be surprised.
 *
 * Those three are stated in words, in a header line, not implied by three shades
 * of the same colour. The severities themselves are never re-decided here — they
 * come off the API, and `api/policies.ts`'s `blocksSave` / `blocksPublish` are the
 * only predicates anyone calls.
 *
 * Each row carries the rule id as a monospace chip, the rule's own title, the
 * runtime consequence, and — where the registry supplied them — the explanation
 * and the Sluice source line that makes the shape a defect rather than a style
 * preference. A finding with a `path` is clickable and asks its owner to reveal the
 * form control responsible, which is how a finding anchored to
 * `spec.match.any[0].resources.tables[1]` becomes an actionable edit instead of a
 * sentence.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import type { BnDiagnosticSeverity } from "../../components/index.js";
import { blocksPublish, blocksSave, type PolicyFinding } from "../../api/policies.js";

/** Detail of `<policy-findings>`'s `finding-click` event. */
export interface PolicyFindingClickDetail {
  rule: string;
  path: string;
}

/** findingsAt returns the findings anchored to exactly one field path, so a
 * bn-field can render its own diagnostic inline instead of sending the reader to
 * a list at the bottom of the page. */
export function findingsAt(findings: PolicyFinding[], path: string): PolicyFinding[] {
  return findings.filter((f) => f.path === path);
}

/** findingsUnder returns the findings anchored at or below a path, which is what
 * a collapsed section badge needs: `spec.match` must show that something inside
 * `spec.match.any[0]` is wrong even while that clause is folded away. */
export function findingsUnder(findings: PolicyFinding[], prefix: string): PolicyFinding[] {
  return findings.filter(
    (f) => !!f.path && (f.path === prefix || f.path.startsWith(`${prefix}.`) || f.path.startsWith(`${prefix}[`)),
  );
}

/** worst returns the most severe finding of a set, or null. Ordering is the
 * workflow ladder, not a judgement of how bad each defect is. */
export function worst(findings: PolicyFinding[]): PolicyFinding | null {
  const order = ["error", "blocking-warning", "warning", "info", "hint"];
  let best: PolicyFinding | null = null;
  for (const f of findings) {
    if (!best || order.indexOf(f.severity) < order.indexOf(best.severity)) best = f;
  }
  return best;
}

/** diagnosticSeverity maps an API severity onto the kit's three-value form
 * vocabulary. `info` and `hint` are reserved for the proxied validators and read
 * as warnings in a form, where there is no fourth tone to spend. */
export function diagnosticSeverity(severity: string): BnDiagnosticSeverity {
  if (severity === "error") return "error";
  if (severity === "blocking-warning") return "blocking-warning";
  return "warning";
}

/** FindingCounts is the per-severity roll-up a header line renders. */
export interface FindingCounts {
  errors: number;
  blocking: number;
  warnings: number;
  other: number;
}

/** countFindings rolls a finding set up by severity. */
export function countFindings(findings: PolicyFinding[]): FindingCounts {
  const out: FindingCounts = { errors: 0, blocking: 0, warnings: 0, other: 0 };
  for (const f of findings) {
    if (f.severity === "error") out.errors++;
    else if (f.severity === "blocking-warning") out.blocking++;
    else if (f.severity === "warning") out.warnings++;
    else out.other++;
  }
  return out;
}

@customElement("policy-findings")
export class PolicyFindings extends LitElement {
  /** The findings, exactly as the API returned them. */
  @property({ attribute: false }) findings: PolicyFinding[] = [];

  /** What to show when there are none. Empty suppresses the element entirely,
   * for a side panel that should not carry a green tick it did not earn. */
  @property() emptyText = "";

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .summary {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
      align-items: baseline;
      margin-bottom: var(--bino-space-sm);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    ul {
      list-style: none;
      margin: 0;
      padding: 0;
      display: grid;
      gap: var(--bino-space-sm);
    }
    li {
      border: 1px solid var(--bino-border);
      border-left-width: 3px;
      border-radius: var(--bino-radius);
      background: var(--bino-surface-subtle);
      padding: var(--bino-space-sm);
    }
    li.error {
      border-left-color: var(--bino-error);
    }
    li.blocking {
      border-left-color: var(--bino-warning);
    }
    li.warning {
      border-left-color: var(--bino-border-hover);
    }
    .top {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: var(--bino-space-xs);
    }
    .rule {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      padding: 0 var(--bino-space-xs);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-surface-inset);
      color: var(--bino-text-muted);
    }
    .title {
      font-size: var(--bino-font-size-base);
      font-weight: 600;
    }
    .gate {
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
    }
    p {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-base);
      max-width: 80ch;
    }
    p.why {
      color: var(--bino-text-secondary);
    }
    .where {
      margin-top: var(--bino-space-xs);
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
      align-items: center;
    }
    button.path {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      color: var(--bino-primary);
      background: none;
      border: none;
      border-radius: var(--bino-radius-sm);
      padding: 0;
      cursor: pointer;
      text-decoration: underline;
    }
    button.path:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
      overflow-wrap: anywhere;
    }
    .none {
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
    }
  `;

  private reveal(f: PolicyFinding) {
    this.dispatchEvent(
      new CustomEvent<PolicyFindingClickDetail>("finding-click", {
        detail: { rule: f.rule, path: f.path ?? "" },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** The one-line statement of what each rung does to the workflow. It is the
   * whole reason this element exists, so it renders above the list rather than in
   * a tooltip. */
  private renderSummary(counts: FindingCounts) {
    const parts: string[] = [];
    if (counts.errors > 0) {
      parts.push(`${counts.errors} error${counts.errors === 1 ? "" : "s"} — blocks saving`);
    }
    if (counts.blocking > 0) {
      parts.push(`${counts.blocking} blocking warning${counts.blocking === 1 ? "" : "s"} — saves, blocks publishing`);
    }
    if (counts.warnings > 0) {
      parts.push(`${counts.warnings} warning${counts.warnings === 1 ? "" : "s"} — blocks nothing`);
    }
    if (counts.other > 0) parts.push(`${counts.other} informational`);
    return html`<div class="summary">${parts.join(" · ")}</div>`;
  }

  private renderFinding(f: PolicyFinding) {
    const tone = blocksSave(f) ? "error" : blocksPublish(f) ? "blocking" : "warning";
    const gate = blocksSave(f)
      ? "blocks saving"
      : blocksPublish(f)
        ? "blocks publishing"
        : "advisory";
    return html`<li class=${tone}>
      <div class="top">
        <code class="rule" title="Policy linter rule ${f.rule}">${f.rule}</code>
        <span class="title">${f.title || f.message}</span>
        <span class="gate">· ${gate}${f.checked_at_publish ? " · re-checked at publish" : ""}</span>
      </div>
      ${f.title ? html`<p>${f.message}</p>` : nothing}
      ${f.explanation ? html`<p class="why">${f.explanation}</p>` : nothing}
      <div class="where">
        ${f.path
          ? html`<button
              class="path"
              type="button"
              @click=${() => this.reveal(f)}
              title="Show the field this finding is about"
            >
              ${f.path}
            </button>`
          : nothing}
        ${f.upstream ? html`<code>${f.upstream}</code>` : nothing}
      </div>
    </li>`;
  }

  override render() {
    if (this.findings.length === 0) {
      return this.emptyText ? html`<p class="none">${this.emptyText}</p>` : nothing;
    }
    const counts = countFindings(this.findings);
    return html`
      ${this.renderSummary(counts)}
      <ul role="list">
        ${this.findings.map((f) => this.renderFinding(f))}
      </ul>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "policy-findings": PolicyFindings;
  }
}
